<template>
  <div class="approval-page">
    <div class="record-top">
      <div class="date-filter">
        <div class="date-row">
          <span class="date-label">时间</span>
          <span class="date-value" @click="openDateRangePicker">{{ dateRange.start || "开始日期" }}</span>
          <span class="date-sep">-</span>
          <span class="date-value" @click="openDateRangePicker">{{ dateRange.end || "结束日期" }}</span>
          <span v-if="dateRange.start || dateRange.end" class="date-clear" @click="clearDateRange">清空</span>
        </div>
      </div>
      <div class="type-tabs record-filters">
        <van-tabs v-model="listType" swipeable @change="onListTypeChange">
          <van-tab v-for="opt in listTypeOptions" :key="String(opt.value)" :title="opt.label" :name="opt.value" />
        </van-tabs>
      </div>
      <div v-if="isSubmittedTab" class="record-filters">
        <van-tabs v-model="currentStatus" swipeable @change="onStatusChange">
          <van-tab v-for="tab in statusTabs" :key="String(tab.value)" :title="tab.label" :name="tab.value" />
        </van-tabs>
      </div>
    </div>

    <div class="approval-list">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list
          v-model="loading"
          :finished="finished"
          finished-text="没有更多了"
          :error.sync="loadError"
          error-text="加载失败，点击重试"
          @load="onLoad"
        >
          <div class="records-wrap">
            <van-empty
              v-if="items.length === 0 && !loading && !loadError"
              image="search"
              :description="listEmptyText"
              class="empty"
            />
            <div
              v-for="item in items"
              :key="item.id"
              :class="['record-card', 'record-accent-card', cardStatusClass(item.status)]"
            >
              <div class="record-card-head">
                <span class="record-card-title">{{ transferRouteLabel(item) }}</span>
                <van-tag :class="['status-tag', statusClass(item)]">{{ statusText(item) }}</van-tag>
              </div>
              <div class="record-row">
                <span class="record-label">学员</span>
                <span class="record-value">{{ studentSummary(item) }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">联系人</span>
                <span class="record-value">{{ item.contact || "-" }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">电话</span>
                <span class="record-value">{{ item.mobile || "-" }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">档案交接</span>
                <span class="record-value">{{ formatRecordTransfer(item.recordTransfer) }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">申请人</span>
                <span class="record-value">{{ item.applyName || "-" }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">申请时间</span>
                <span class="record-value">{{ item.createTime || "-" }}</span>
              </div>
              <div v-if="item.executeTime" class="record-row">
                <span class="record-label">执行时间</span>
                <span class="record-value">{{ item.executeTime }}</span>
              </div>
              <div v-if="item.remark" class="record-row">
                <span class="record-label">备注</span>
                <span class="record-value">{{ item.remark }}</span>
              </div>

              <div v-if="normalizeList(item.studentList).length" class="collapse-section">
                <div class="collapse-head" @click="toggleSectionExpand(item.id, 'student')">
                  <span class="collapse-title">学员明细</span>
                  <div class="collapse-head-right">
                    <span v-if="!isSectionExpanded(item.id, 'student')" class="collapse-summary">
                      {{ normalizeList(item.studentList).length }} 人
                    </span>
                    <van-icon :name="isSectionExpanded(item.id, 'student') ? 'arrow-up' : 'arrow-down'" class="collapse-arrow" />
                  </div>
                </div>
                <div v-show="isSectionExpanded(item.id, 'student')" class="collapse-body">
                  <div
                    v-for="(stu, stuIdx) in normalizeList(item.studentList)"
                    :key="`stu-${item.id}-${stuIdx}`"
                    class="detail-item-card"
                  >
                    <div class="record-row">
                      <span class="record-label">学员</span>
                      <span class="record-value">{{ stu.studentName || "-" }}</span>
                    </div>
                    <div class="record-row">
                      <span class="record-label">转中心日期</span>
                      <span class="record-value">{{ stu.transferDate || "-" }}</span>
                    </div>
                    <div
                      v-for="(pkg, pkgIdx) in normalizeList(stu.coursePackageList)"
                      :key="`pkg-${item.id}-${stuIdx}-${pkgIdx}`"
                      class="detail-sub-card"
                    >
                      <div class="record-row">
                        <span class="record-label">课包</span>
                        <span class="record-value">{{ pkg.coursePackageName || "-" }}</span>
                      </div>
                      <div class="record-row">
                        <span class="record-label">转让课时</span>
                        <span class="record-value">{{ pkg.transferCourseNum != null ? pkg.transferCourseNum : "-" }}</span>
                      </div>
                      <div class="record-row">
                        <span class="record-label">转让金额</span>
                        <span class="record-value amount">{{ formatAmount(pkg.transferCourseAmount) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="visibleApprovalSteps(item.approvalDetailList).length" class="collapse-section">
                <div class="collapse-head" @click="toggleSectionExpand(item.id, 'approval')">
                  <span class="collapse-title">审批进度</span>
                  <van-icon :name="isSectionExpanded(item.id, 'approval') ? 'arrow-up' : 'arrow-down'" class="collapse-arrow" />
                </div>
                <div v-show="isSectionExpanded(item.id, 'approval')" class="collapse-body">
                  <div
                    v-for="(step, stepIdx) in visibleApprovalSteps(item.approvalDetailList)"
                    :key="`approval-${item.id}-${stepIdx}`"
                    :class="['approval-step-card', approvalStepCardClass(step.status)]"
                  >
                    <div class="record-row">
                      <span class="record-label">审批人</span>
                      <span class="record-value">{{ step.approveName || "-" }}</span>
                    </div>
                    <div class="record-row">
                      <span class="record-label">状态</span>
                      <span class="record-value" :class="approvalStepStatusClass(step.status)">
                        {{ approvalDetailStatusText(step.status) }}
                      </span>
                    </div>
                    <div v-if="step.approveTime" class="record-row">
                      <span class="record-label">审批时间</span>
                      <span class="record-value">{{ step.approveTime }}</span>
                    </div>
                    <div v-if="step.remark" class="record-row">
                      <span class="record-label">备注</span>
                      <span class="record-value">{{ step.remark }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="isSubmittedTab && Number(item.status) === 0" class="record-actions">
                <van-button
                  round
                  block
                  plain
                  type="danger"
                  :loading="cancelingId === String(item.id)"
                  @click="cancelApply(item)"
                >
                  取消申请
                </van-button>
              </div>
              <div v-if="isPendingTab && Number(item.status) === 0" class="audit-actions">
                <van-button
                  round
                  block
                  plain
                  type="danger"
                  class="action-btn action-btn-reject"
                  :loading="rejectingId === String(item.id)"
                  @click="openRejectDialog(item)"
                >
                  拒绝
                </van-button>
                <van-button
                  round
                  block
                  type="primary"
                  class="action-btn action-btn-approve"
                  :loading="approvingId === String(item.id)"
                  @click="submitApprove(item)"
                >
                  同意
                </van-button>
              </div>
            </div>
          </div>
        </van-list>
      </van-pull-refresh>
    </div>

    <div v-if="isSubmittedTab" class="page-bottom-bar">
      <van-button round block type="primary" class="action-btn-approve" @click="openApplyDialog">
        申请转中心
      </van-button>
    </div>

    <van-popup v-model="applyDialog.show" position="bottom" round :z-index="1100" :style="{ height: '88vh' }">
      <div class="booking-popup-wrap">
        <div class="popup-head">
          <div class="popup-title">申请转中心</div>
          <van-icon name="cross" class="popup-close" @click="closeApplyDialog" />
        </div>
        <div class="booking-popup-scroll">
          <van-field
            readonly
            clickable
            label="原中心"
            :value="sourceDepartmentLabel"
            placeholder="请选择原中心"
            required
            @click="openDepartmentPicker('source')"
          />
          <van-field
            readonly
            clickable
            label="目标中心"
            :value="targetDepartmentLabel"
            placeholder="请选择目标中心"
            required
            @click="openDepartmentPicker('target')"
          />
          <van-field v-model.trim="applyDialog.form.contact" label="联系人" placeholder="请输入联系人" required />
          <van-field v-model.trim="applyDialog.form.mobile" label="电话" placeholder="请输入电话" type="tel" required />
          <div class="apply-section-title apply-section-title--compact">档案交接</div>
          <div class="record-transfer-checkboxes">
            <van-checkbox-group v-model="applyDialog.form.recordTransferList">
              <van-checkbox
                v-for="opt in recordTransferOptions"
                :key="opt.value"
                :name="opt.value"
                shape="square"
                icon-size="16px"
              >
                {{ opt.text }}
              </van-checkbox>
            </van-checkbox-group>
          </div>
          <van-field v-model.trim="applyDialog.form.remark" label="备注" type="textarea" rows="2" autosize placeholder="选填" />

          <div class="apply-section-head">
            <span class="apply-section-title">学员明细</span>
            <span class="apply-section-add" @click="addApplyStudent">+ 添加学员</span>
          </div>
          <div
            v-for="(stu, stuIdx) in applyDialog.form.studentList"
            :key="stu._key"
            class="student-package-card"
          >
            <div class="student-package-card-head">
              <span class="student-package-card-title">学员 {{ stuIdx + 1 }}</span>
              <van-button
                v-if="applyDialog.form.studentList.length > 1"
                plain
                hairline
                round
                size="mini"
                type="danger"
                @click="removeApplyStudent(stuIdx)"
              >
                删除
              </van-button>
            </div>
            <van-field
              readonly
              clickable
              label="学员"
              :value="stu.studentName"
              placeholder="请选择学员"
              required
              @click="openStudentPicker(stuIdx)"
            />
            <van-field
              readonly
              clickable
              label="转中心日期"
              :value="stu.transferDate"
              placeholder="请选择转中心日期"
              required
              @click="openTransferDatePicker(stuIdx)"
            />
            <div v-if="stu.packageLoading" class="package-loading-wrap">
              <van-loading size="16px">加载课包信息...</van-loading>
            </div>
            <div v-else-if="stu.coursePackageList.length" class="student-package-block">
              <div class="student-package-block-title">课包明细</div>
              <div
                v-for="(pkg, pkgIdx) in stu.coursePackageList"
                :key="`apply-pkg-${stu._key}-${pkgIdx}`"
                class="student-package-item"
              >
                <div class="student-package-item-name">{{ pkg.coursePackageName || `课包${pkgIdx + 1}` }}</div>
                <van-field v-model.trim="pkg.transferCourseNum" label="转让课时" type="number" placeholder="请输入" required />
                <van-field v-model.trim="pkg.transferCourseAmount" label="转让金额" type="number" placeholder="请输入" required />
              </div>
            </div>
            <van-empty v-else-if="stu.studentId" description="该学员暂无可转课包" class="panel-empty" />
          </div>
        </div>
        <div class="popup-actions">
          <van-button
            round
            block
            type="primary"
            class="theme-btn"
            :loading="applyDialog.submitting"
            @click="submitApply"
          >
            提交申请
          </van-button>
        </div>
      </div>
    </van-popup>

    <van-popup v-model="studentPicker.show" position="bottom" round :z-index="1120" :style="{ height: '70vh' }">
      <div class="student-picker-inner">
        <div class="popup-head">
          <div class="popup-title">选择学员</div>
          <van-icon name="cross" class="popup-close" @click="closeStudentPicker" />
        </div>
        <van-search
          v-model="studentPicker.keyword"
          placeholder="搜索学员姓名"
          shape="round"
          @search="onStudentSearch"
          @input="onStudentKeywordInput"
        />
        <div class="student-list-wrap">
          <van-loading v-if="studentPicker.loading" class="panel-loading" size="20px" />
          <van-empty v-else-if="!studentPicker.list.length" description="暂无学员" class="panel-empty" />
          <div
            v-for="row in studentPicker.list"
            :key="row._pickerKey"
            :class="['student-item', { active: isStudentPickerActive(row) }]"
            @click="selectStudent(row)"
          >
            <div class="student-item-name">{{ row.stuName || row.studentName || "-" }}</div>
            <div class="student-item-sub">{{ formatStudentPickerSub(row) }}</div>
          </div>
        </div>
      </div>
    </van-popup>

    <van-popup v-model="departmentPicker.show" position="bottom" :z-index="1140">
      <van-picker
        show-toolbar
        :title="departmentPicker.target === 'source' ? '选择原中心' : '选择目标中心'"
        :columns="topDepartmentOptions"
        @confirm="onConfirmDepartment"
        @cancel="departmentPicker.show = false"
      />
    </van-popup>

    <van-popup v-model="transferDatePicker.show" position="bottom" :z-index="1160">
      <van-datetime-picker
        v-model="transferDatePicker.value"
        type="date"
        title="选择转中心日期"
        :min-date="transferDatePicker.minDate"
        :max-date="transferDatePicker.maxDate"
        @confirm="onConfirmTransferDate"
        @cancel="transferDatePicker.show = false"
      />
    </van-popup>

    <van-calendar
      v-model="dateRangePickerShow"
      type="range"
      :min-date="dateRangePickerMinDate"
      :max-date="dateRangePickerMaxDate"
      :default-date="dateRangePickerDefault"
      color="rgb(242,124,52)"
      @confirm="onConfirmDateRange"
    />

    <van-dialog
      v-model="rejectDialog.show"
      title="拒绝原因"
      show-cancel-button
      confirm-button-text="确认拒绝"
      cancel-button-text="取消"
      @confirm="submitReject"
    >
      <div class="dialog-body">
        <van-field v-model.trim="rejectDialog.reason" type="textarea" rows="3" autosize placeholder="请输入拒绝原因" />
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { Toast, Dialog } from "vant"
import {
  fetchTransferCenterList,
  fetchTransferCenterApprovalList,
  applyTransferCenter,
  cancelTransferCenterApply,
  approveTransferCenterPass,
  approveTransferCenterReject,
  fetchStudentPackageRemain
} from "@/api/transferCenter"
import { fetchCustomerList, fetchTopDepartments } from "@/api/customer"
import { ensureCurrentStaffProfile, getCurrentTopDepartmentId } from "@/store/currentStaff"
import { resetPageScrollAfterPaint } from "@/utils/scroll"

const CUSTOMER_STATUS_LIST = [0, 1, 2, 3, 4]
const RECORD_TRANSFER_OPTIONS = [
  { text: "学生合同", value: "1" },
  { text: "所有课卡、作业、Study record", value: "2" }
]

function normalizeList(res) {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res?.list)) return res.list
  return []
}

function formatDateOnly(d) {
  const dt = d instanceof Date ? d : new Date(d)
  const p = n => String(n).padStart(2, "0")
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`
}

function createEmptyApplyStudent() {
  return {
    _key: `stu-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    studentId: null,
    studentName: "",
    transferDate: formatDateOnly(new Date()),
    packageLoading: false,
    coursePackageList: []
  }
}

function resolveStudentPackageRemain(res) {
  let raw = res && typeof res === "object" && res.data !== undefined && res.data !== null ? res.data : res
  if (Array.isArray(raw)) return raw[0] || {}
  return raw && typeof raw === "object" ? raw : {}
}

function normalizePackageRemainList(source) {
  if (!source || typeof source !== "object") return []
  return normalizeList(source.coursePackageList).filter(
    x => x && x.id !== null && x.id !== undefined && x.id !== ""
  )
}

export default {
  name: "TransferCenterApproval",
  data() {
    return {
      listType: 1,
      listTypeOptions: [
        { label: "已提交", value: 1 },
        { label: "待处理", value: 2 },
        { label: "已处理", value: 3 }
      ],
      currentStatus: "all",
      statusTabs: [
        { label: "全部", value: "all" },
        { label: "审批中", value: 0 },
        { label: "已同意", value: 1 },
        { label: "已拒绝", value: 2 },
        { label: "已取消", value: 3 }
      ],
      dateRange: { start: "", end: "" },
      dateRangePickerShow: false,
      dateRangePickerMinDate: new Date(1970, 0, 1),
      dateRangePickerMaxDate: new Date(2100, 11, 31),
      items: [],
      loginTopDepartmentId: null,
      topDepartmentOptions: [],
      recordTransferOptions: RECORD_TRANSFER_OPTIONS,
      page: 1,
      pageSize: 20,
      loading: false,
      finished: false,
      refreshing: false,
      loadError: false,
      scrollToTopAfterLoad: false,
      cancelingId: "",
      rejectingId: "",
      approvingId: "",
      sectionExpanded: {},
      rejectDialog: { show: false, id: "", reason: "" },
      applyDialog: {
        show: false,
        submitting: false,
        form: {
          sourceTopDepartmentId: null,
          targetTopDepartmentId: null,
          contact: "",
          mobile: "",
          recordTransferList: [],
          remark: "",
          studentList: [createEmptyApplyStudent()]
        }
      },
      departmentPicker: { show: false, target: "source" },
      studentPicker: {
        show: false,
        index: 0,
        loading: false,
        keyword: "",
        keywordTimer: null,
        list: []
      },
      transferDatePicker: {
        show: false,
        index: 0,
        value: new Date(),
        minDate: new Date(1970, 0, 1),
        maxDate: new Date(2100, 11, 31)
      }
    }
  },
  computed: {
    dateRangePickerDefault() {
      const { start, end } = this.dateRange
      if (!start && !end) return []
      const s = start ? this.parseDateOnly(start) : null
      const e = end ? this.parseDateOnly(end) : null
      if (s && e) return [s, e]
      return s ? [s, s] : []
    },
    isSubmittedTab() {
      return Number(this.listType) === 1
    },
    isPendingTab() {
      return Number(this.listType) === 2
    },
    listEmptyText() {
      if (this.isSubmittedTab) return "暂无数据"
      if (this.isPendingTab) return "暂无待处理数据"
      return "暂无已处理数据"
    },
    sourceDepartmentLabel() {
      return this.getDepartmentLabel(this.applyDialog.form.sourceTopDepartmentId)
    },
    targetDepartmentLabel() {
      return this.getDepartmentLabel(this.applyDialog.form.targetTopDepartmentId)
    }
  },
  mounted() {
    const qType = String(this.$route?.query?.type || "").trim()
    if (qType === "approval") this.listType = 2
    this.ensureLoginStaffInfo()
    this.loadTopDepartments()
    this.onRefresh()
  },
  methods: {
    formatAmount(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return "-"
      return `￥${n.toFixed(2)}`
    },
    parseDateOnly(v) {
      if (!v) return new Date()
      const d = new Date(String(v).replace(/-/g, "/"))
      return Number.isNaN(d.getTime()) ? new Date() : d
    },
    normalizeList(value) {
      return normalizeList(value)
    },
    async ensureLoginStaffInfo() {
      try {
        const staff = await ensureCurrentStaffProfile()
        const dept = staff?.topDepartment
        const id = typeof dept === "object" ? (dept.weId ?? dept.id ?? dept.topDepartmentId) : dept
        if (id !== null && id !== undefined && id !== "") {
          this.loginTopDepartmentId = id
        } else {
          const cachedId = getCurrentTopDepartmentId()
          if (cachedId !== null && cachedId !== undefined && cachedId !== "") {
            this.loginTopDepartmentId = cachedId
          }
        }
      } catch (e) {
        // ignore
      }
    },
    async loadTopDepartments() {
      try {
        const res = await fetchTopDepartments()
        const list = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : [])
        this.topDepartmentOptions = list
          .map(item => ({ text: item.weName || item.name || "-", value: item.weId ?? item.id }))
          .filter(x => x.value !== null && x.value !== undefined && x.value !== "")
      } catch (e) {
        this.topDepartmentOptions = []
      }
    },
    getDepartmentLabel(id) {
      if (id === null || id === undefined || id === "") return ""
      const hit = this.topDepartmentOptions.find(x => String(x.value) === String(id))
      return hit ? hit.text : String(id)
    },
    transferRouteLabel(item) {
      const source = (item?.sourceTopDepartmentName || "-").trim()
      const target = (item?.targetTopDepartmentName || "-").trim()
      return `${source} → ${target}`
    },
    studentSummary(item) {
      const list = normalizeList(item?.studentList)
      if (!list.length) return "-"
      if (list.length === 1) return list[0].studentName || "-"
      return `${list[0].studentName || "学员"} 等${list.length}人`
    },
    formatRecordTransfer(value) {
      let parts = []
      if (Array.isArray(value)) {
        parts = value.map(v => String(v).trim()).filter(Boolean)
      } else {
        const raw = value === null || value === undefined ? "" : String(value)
        if (!raw.trim()) return "-"
        parts = raw.split(/[,，]/).map(x => x.trim()).filter(Boolean)
      }
      if (!parts.length) return "-"
      const labels = parts.map(v => {
        const hit = RECORD_TRANSFER_OPTIONS.find(x => x.value === v)
        return hit ? hit.text : v
      })
      return labels.join("、")
    },
    statusText(item) {
      const s = Number(item?.status)
      const map = { 0: "审批中", 1: "已同意", 2: "已拒绝", 3: "已取消" }
      return item?.statusName || map[s] || "-"
    },
    approvalDetailStatusText(status) {
      const s = Number(status)
      if (s === 0) return "审批中"
      if (s === 1) return "已同意"
      if (s === 2) return "已拒绝"
      return "-"
    },
    approvalStepStatusClass(status) {
      const s = Number(status)
      if (s === 1) return "approval-step-approved"
      if (s === 2) return "approval-step-rejected"
      return "approval-step-pending"
    },
    approvalStepCardClass(status) {
      const s = Number(status)
      if (s === 1) return "approval-step-card-approved"
      if (s === 2) return "approval-step-card-rejected"
      return "approval-step-card-pending"
    },
    visibleApprovalSteps(list) {
      const steps = normalizeList(list)
      const result = []
      for (const step of steps) {
        result.push(step)
        if (Number(step?.status) === 2) break
      }
      return result
    },
    statusClass(item) {
      const s = Number(item?.status)
      if (s === 0) return "status-pending"
      if (s === 1) return "status-approved"
      if (s === 2) return "status-rejected"
      return "status-cancel"
    },
    cardStatusClass(status) {
      const s = Number(status)
      if (s === 0) return "card-pending"
      if (s === 1) return "card-approved"
      if (s === 2) return "card-rejected"
      if (s === 3) return "card-cancel"
      return "card-pending"
    },
    itemSectionKey(itemId, section) {
      return `${itemId || "unknown"}-${section}`
    },
    isSectionExpanded(itemId, section) {
      return !!this.sectionExpanded[this.itemSectionKey(itemId, section)]
    },
    toggleSectionExpand(itemId, section) {
      const key = this.itemSectionKey(itemId, section)
      this.$set(this.sectionExpanded, key, !this.sectionExpanded[key])
    },
    onListTypeChange(type) {
      this.listType = Number(type) || 1
      this.onRefresh(true)
    },
    onStatusChange(v) {
      this.currentStatus = v === "all" ? "all" : Number(v)
      this.onRefresh(true)
    },
    openDateRangePicker() {
      this.dateRangePickerShow = true
    },
    onConfirmDateRange(dates) {
      this.dateRangePickerShow = false
      const arr = Array.isArray(dates) ? dates : [dates]
      const start = arr[0]
      const end = arr[1] || arr[0]
      this.dateRange.start = start ? formatDateOnly(start) : ""
      this.dateRange.end = end ? formatDateOnly(end) : ""
      this.onRefresh()
    },
    clearDateRange() {
      if (!this.dateRange.start && !this.dateRange.end) return
      this.dateRange.start = ""
      this.dateRange.end = ""
      this.onRefresh()
    },
    onRefresh(scrollToTop = false) {
      if (scrollToTop) {
        this.scrollToTopAfterLoad = true
        resetPageScrollAfterPaint(this.$el)
      }
      this.finished = false
      this.loading = true
      this.refreshing = true
      this.page = 1
      this.items = []
      this.onLoad()
    },
    buildListPayload() {
      const payload = {
        createStartTime: this.dateRange.start || undefined,
        createEndTime: this.dateRange.end || undefined,
        displayLength: this.pageSize,
        displayStart: this.page
      }
      const listType = Number(this.listType)
      if (listType === 1) {
        if (this.currentStatus !== "all") payload.status = Number(this.currentStatus)
      } else if (listType === 2) {
        payload.approvalStatusList = [0]
      } else if (listType === 3) {
        payload.approvalStatusList = [1, 2, 3]
      }
      return payload
    },
    async onLoad() {
      this.loadError = false
      try {
        const payload = this.buildListPayload()
        const listType = Number(this.listType)
        const fetchList = listType === 1 ? fetchTransferCenterList : fetchTransferCenterApprovalList
        const res = await fetchList(payload)
        const list = normalizeList(res)
        if (this.refreshing) this.items = []
        this.items = this.items.concat(list)
        this.finished = list.length < this.pageSize
        this.page += 1
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
        this.refreshing = false
        if (this.scrollToTopAfterLoad) {
          this.scrollToTopAfterLoad = false
          this.$nextTick(() => resetPageScrollAfterPaint(this.$el))
        }
      }
    },
    async cancelApply(item) {
      const id = item?.id
      if (!id) return Toast.fail("缺少申请ID")
      try {
        await Dialog.confirm({ message: "确定取消申请?" })
      } catch (e) {
        return
      }
      this.cancelingId = String(id)
      try {
        await cancelTransferCenterApply({ id })
        Toast.success("已取消申请")
        this.onRefresh()
      } finally {
        this.cancelingId = ""
      }
    },
    openRejectDialog(item) {
      const id = item?.id
      if (!id) return Toast.fail("缺少申请ID")
      this.rejectDialog.id = String(id)
      this.rejectDialog.reason = ""
      this.rejectDialog.show = true
    },
    async submitReject() {
      const id = this.rejectDialog.id
      if (!id) return Toast.fail("缺少申请ID")
      this.rejectingId = String(id)
      try {
        await approveTransferCenterReject({
          id,
          rejectReason: (this.rejectDialog.reason || "").trim() || undefined
        })
        Toast.success("已拒绝")
        this.rejectDialog.show = false
        this.onRefresh()
      } finally {
        this.rejectingId = ""
      }
    },
    async submitApprove(item) {
      const id = item?.id
      if (!id) return Toast.fail("缺少申请ID")
      try {
        await Dialog.confirm({ message: "确定同意该转中心申请?" })
      } catch (e) {
        return
      }
      this.approvingId = String(id)
      try {
        await approveTransferCenterPass({ id })
        Toast.success("已同意")
        this.onRefresh()
      } finally {
        this.approvingId = ""
      }
    },
    resetApplyForm() {
      this.applyDialog.form = {
        sourceTopDepartmentId: this.loginTopDepartmentId ?? null,
        targetTopDepartmentId: null,
        contact: "",
        mobile: "",
        recordTransferList: [],
        remark: "",
        studentList: [createEmptyApplyStudent()]
      }
    },
    openApplyDialog() {
      this.resetApplyForm()
      this.applyDialog.show = true
    },
    closeApplyDialog() {
      this.applyDialog.show = false
    },
    openDepartmentPicker(target) {
      if (!this.topDepartmentOptions.length) return Toast.fail("暂无中心列表")
      this.departmentPicker.target = target
      this.departmentPicker.show = true
    },
    onConfirmDepartment(values, indexes) {
      const idx = Array.isArray(indexes) ? indexes[0] : indexes
      const opt = this.topDepartmentOptions[idx]
      if (!opt) {
        this.departmentPicker.show = false
        return
      }
      if (this.departmentPicker.target === "source") {
        this.applyDialog.form.sourceTopDepartmentId = opt.value
        this.applyDialog.form.studentList.forEach(stu => {
          stu.studentId = null
          stu.studentName = ""
          stu.coursePackageList = []
        })
      } else {
        this.applyDialog.form.targetTopDepartmentId = opt.value
      }
      this.departmentPicker.show = false
    },
    addApplyStudent() {
      this.applyDialog.form.studentList.push(createEmptyApplyStudent())
    },
    removeApplyStudent(index) {
      if (this.applyDialog.form.studentList.length <= 1) return Toast.fail("至少保留一名学员")
      this.applyDialog.form.studentList.splice(index, 1)
    },
    openStudentPicker(index) {
      if (!this.applyDialog.form.sourceTopDepartmentId) return Toast.fail("请先选择原中心")
      this.studentPicker.index = index
      this.studentPicker.keyword = ""
      this.studentPicker.list = []
      this.studentPicker.show = true
      this.searchStudents()
    },
    closeStudentPicker() {
      this.studentPicker.keyword = ""
      this.studentPicker.show = false
    },
    isStudentPickerActive(row) {
      const index = this.studentPicker.index
      const stu = this.applyDialog.form.studentList[index]
      if (!stu?.studentId) return false
      const studentId = row?.studentId
      return studentId !== null && studentId !== undefined && String(stu.studentId) === String(studentId)
    },
    formatStudentPickerSub(row) {
      const parent = (row.parentName || row.name || "").trim() || "-"
      const mobile = (row.mobile || "").trim() || "-"
      const dept = (row.topDepartmentName || row.departmentName || "").trim() || "-"
      return `家长：${parent} · ${mobile} · ${dept}`
    },
    normalizeStudentPickerRow(row, idx) {
      return {
        ...row,
        parentName: (row.name || row.parentName || "").trim(),
        topDepartmentName: (row.topDepartmentName || row.departmentName || "").trim(),
        _pickerKey: `${row.studentId}-${idx}`
      }
    },
    onStudentKeywordInput() {
      if (this.studentPicker.keywordTimer) clearTimeout(this.studentPicker.keywordTimer)
      this.studentPicker.keywordTimer = setTimeout(() => this.searchStudents(), 350)
    },
    onStudentSearch() {
      this.searchStudents()
    },
    async searchStudents() {
      this.studentPicker.loading = true
      try {
        const keyword = (this.studentPicker.keyword || "").trim()
        const res = await fetchCustomerList({
          displayStart: 1,
          displayLength: 50,
          allName: keyword || undefined,
          statusList: CUSTOMER_STATUS_LIST,
          topDepartmentId: this.applyDialog.form.sourceTopDepartmentId || undefined
        })
        const list = Array.isArray(res?.data) ? res.data : []
        this.studentPicker.list = list
          .filter(row => row?.studentId !== null && row?.studentId !== undefined && row?.studentId !== "")
          .map((row, idx) => this.normalizeStudentPickerRow(row, idx))
      } catch (e) {
        this.studentPicker.list = []
      } finally {
        this.studentPicker.loading = false
      }
    },
    async selectStudent(row) {
      const studentId = row?.studentId
      if (!studentId) return Toast.fail("学员数据无效")
      const index = this.studentPicker.index
      const stu = this.applyDialog.form.studentList[index]
      if (!stu) return
      stu.studentId = studentId
      stu.studentName = (row.stuName || row.studentName || "").trim() || "-"
      this.$set(stu, "coursePackageList", [])
      this.studentPicker.show = false
      await this.loadStudentPackages(index)
    },
    async loadStudentPackages(index) {
      const stu = this.applyDialog.form.studentList[index]
      if (!stu?.studentId) return
      this.$set(stu, "packageLoading", true)
      try {
        const res = await fetchStudentPackageRemain({
          studentId: stu.studentId,
          topDepartmentId: this.applyDialog.form.sourceTopDepartmentId || undefined
        })
        const info = resolveStudentPackageRemain(res)
        const pkgList = normalizePackageRemainList(info)
        const mapped = pkgList.map(pkg => ({
          coursePackageId: pkg.coursePackageId,
          sourceOrderCoursePackageId: pkg.id,
          orderId: pkg.orderId ?? info.orderId ?? null,
          coursePackageName: pkg.coursePackageName || "",
          transferCourseNum: pkg.remainCourseNum != null ? String(pkg.remainCourseNum) : "",
          transferCourseAmount: pkg.remainCourseAmount != null ? String(pkg.remainCourseAmount) : ""
        }))
        this.$set(stu, "coursePackageList", mapped)
      } catch (e) {
        this.$set(stu, "coursePackageList", [])
      } finally {
        this.$set(stu, "packageLoading", false)
      }
    },
    openTransferDatePicker(index) {
      this.transferDatePicker.index = index
      const stu = this.applyDialog.form.studentList[index]
      this.transferDatePicker.value = stu?.transferDate
        ? this.parseDateOnly(stu.transferDate)
        : new Date()
      this.transferDatePicker.show = true
    },
    onConfirmTransferDate(value) {
      const stu = this.applyDialog.form.studentList[this.transferDatePicker.index]
      if (stu) stu.transferDate = formatDateOnly(value)
      this.transferDatePicker.show = false
    },
    validateApplyForm() {
      const form = this.applyDialog.form
      if (!form.sourceTopDepartmentId) return "请选择原中心"
      if (!form.targetTopDepartmentId) return "请选择目标中心"
      if (String(form.sourceTopDepartmentId) === String(form.targetTopDepartmentId)) return "原中心与目标中心不能相同"
      if (!(form.contact || "").trim()) return "请输入联系人"
      if (!(form.mobile || "").trim()) return "请输入电话"
      const studentList = Array.isArray(form.studentList) ? form.studentList : []
      if (!studentList.length) return "请添加学员"
      for (let i = 0; i < studentList.length; i++) {
        const stu = studentList[i]
        const n = i + 1
        if (!stu.studentId) return `学员${n}：请选择学员`
        if (!(stu.transferDate || "").trim()) return `学员${n}：请选择转中心日期`
        const pkgList = Array.isArray(stu.coursePackageList) ? stu.coursePackageList : []
        if (!pkgList.length) return `学员${n}：暂无可转课包`
        for (let j = 0; j < pkgList.length; j++) {
          const pkg = pkgList[j]
          const pkgName = (pkg.coursePackageName || "").trim() || `第${j + 1}个课包`
          const transferCourseNum = Number(pkg.transferCourseNum)
          if (pkg.transferCourseNum === "" || pkg.transferCourseNum === null || pkg.transferCourseNum === undefined) {
            return `${pkgName}：请输入转让课时`
          }
          if (!Number.isFinite(transferCourseNum) || transferCourseNum < 0) return `${pkgName}：请输入正确的转让课时`
          const transferCourseAmount = Number(pkg.transferCourseAmount)
          if (pkg.transferCourseAmount === "" || pkg.transferCourseAmount === null || pkg.transferCourseAmount === undefined) {
            return `${pkgName}：请输入转让金额`
          }
          if (!Number.isFinite(transferCourseAmount) || transferCourseAmount < 0) return `${pkgName}：请输入正确的转让金额`
        }
      }
      return ""
    },
    async submitApply() {
      if (this.applyDialog.submitting) return
      const err = this.validateApplyForm()
      if (err) return Toast.fail(err)
      const form = this.applyDialog.form
      const payload = {
        sourceTopDepartmentId: Number(form.sourceTopDepartmentId),
        targetTopDepartmentId: Number(form.targetTopDepartmentId),
        contact: (form.contact || "").trim(),
        mobile: (form.mobile || "").trim(),
        recordTransfer: (form.recordTransferList || []).map(v => Number(v)).filter(v => Number.isFinite(v)),
        remark: (form.remark || "").trim() || undefined,
        studentList: form.studentList.map(stu => ({
          studentId: stu.studentId,
          transferDate: (stu.transferDate || "").trim(),
          coursePackageList: stu.coursePackageList.map(pkg => ({
            coursePackageId: pkg.coursePackageId,
            sourceOrderCoursePackageId: pkg.sourceOrderCoursePackageId,
            sourceOrderId: pkg.orderId,
            transferCourseNum: Number(pkg.transferCourseNum),
            transferCourseAmount: Number(pkg.transferCourseAmount)
          }))
        }))
      }
      this.applyDialog.submitting = true
      try {
        await applyTransferCenter(payload)
        Toast.success("申请已提交")
        this.applyDialog.show = false
        this.onRefresh()
      } finally {
        this.applyDialog.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.approval-page {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(--app-tabbar-offset, 50px));
  background: #f7f8fa;
}

.record-top {
  background: #fff;
  padding: 10px 12px 0;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
}

.date-filter {
  margin-bottom: 8px;
}

.date-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.date-label {
  color: #969799;
}

.date-value {
  color: #323233;
}

.date-clear {
  margin-left: auto;
  color: rgb(242, 124, 52);
}

.approval-list {
  flex: 1;
  padding: 8px 12px calc(80px + var(--app-safe-area-bottom, 0px));
}

.records-wrap {
  min-height: 120px;
}

.record-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.record-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.record-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}

.record-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  font-size: 13px;
}

.record-label {
  color: #969799;
  flex-shrink: 0;
}

.record-value {
  color: #323233;
  text-align: right;
  word-break: break-all;
}

.record-value.amount {
  color: rgb(242, 124, 52);
  font-weight: 600;
}

.audit-actions,
.record-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.page-bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: var(--app-tabbar-offset, 50px);
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
  z-index: 90;
}

.action-btn-approve {
  color: #fff;
  background: rgb(242, 124, 52);
  border-color: rgb(242, 124, 52);
}

.detail-popup-inner,
.booking-popup-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.popup-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #ebedf0;
}

.popup-title {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}

.popup-close {
  font-size: 18px;
  color: #969799;
}

.booking-popup-scroll,
.popup-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.collapse-section {
  margin-top: 10px;
  border-top: 1px dashed #f0f0f0;
  padding-top: 8px;
}

.collapse-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.collapse-title {
  font-size: 13px;
  font-weight: 600;
}

.collapse-head-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.collapse-summary {
  font-size: 12px;
  color: #969799;
}

.collapse-arrow {
  color: #969799;
}

.collapse-body {
  margin-top: 8px;
}

.detail-item-card {
  background: #fafafa;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
}

.student-package-card {
  margin: 0 12px 12px;
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.student-package-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.student-package-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #323233;
}

.student-package-block {
  border-top: 1px dashed #ebedf0;
  margin-top: 4px;
  padding-top: 4px;
}

.student-package-block-title {
  padding: 8px 16px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #646566;
}

.student-package-item {
  margin: 0 12px 8px;
  padding: 8px 10px;
  background: #f7f8fa;
  border-radius: 8px;
}

.student-package-item-name {
  padding: 0 6px 6px;
  font-size: 13px;
  font-weight: 600;
  color: #323233;
}

.detail-sub-card {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #ebedf0;
}

.apply-section-title {
  padding: 12px 16px 6px;
  font-size: 14px;
  font-weight: 600;
  color: #323233;
}

.apply-section-title--compact {
  padding-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
}

.apply-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 6px;
}

.apply-section-add {
  color: rgb(242, 124, 52);
  font-size: 13px;
}

.apply-section-subtitle {
  padding: 0 16px 6px;
  font-size: 13px;
  color: #646566;
}

.apply-student-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 6px;
}

.record-transfer-checkboxes {
  padding: 0 16px 12px;
  font-size: 13px;
}

.record-transfer-checkboxes :deep(.van-checkbox) {
  display: flex;
  align-items: flex-start;
  margin: 0 0 8px;
  font-size: 13px;
}

.record-transfer-checkboxes :deep(.van-checkbox__label) {
  font-size: 13px;
  line-height: 1.45;
  white-space: normal;
  word-break: break-all;
  color: #646566;
}

.record-transfer-checkboxes :deep(.van-checkbox__icon) {
  font-size: 16px;
}

.package-loading-wrap {
  padding: 12px 16px;
  text-align: center;
}

.popup-actions {
  flex-shrink: 0;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #ebedf0;
}

.theme-btn {
  background: rgb(242, 124, 52);
  border-color: rgb(242, 124, 52);
}

.student-picker-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.student-list-wrap {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 12px 12px;
}

.student-item {
  padding: 12px;
  border-radius: 10px;
  background: #f7f8fa;
  margin-bottom: 8px;
}

.student-item.active {
  background: rgba(242, 124, 52, 0.08);
}

.student-item-name {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}

.student-item-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #969799;
  line-height: 1.5;
  word-break: break-all;
}

.panel-loading,
.panel-empty {
  padding: 24px 0;
  text-align: center;
}

.dialog-body {
  padding: 12px 16px 4px;
}

.action-btn {
  flex: 1;
  height: 40px;
}

.action-btn-reject {
  color: #ee0a24;
  border-color: #ee0a24;
}

.approval-step-card {
  margin-top: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 0 10px 10px;
  background: #fff;
}

.approval-step-card-pending {
  background: rgba(245, 158, 11, 0.06);
  border-color: rgba(245, 158, 11, 0.28);
}

.approval-step-card-approved {
  background: rgba(34, 160, 107, 0.06);
  border-color: rgba(34, 160, 107, 0.28);
}

.approval-step-card-rejected {
  background: rgba(214, 93, 140, 0.06);
  border-color: rgba(214, 93, 140, 0.28);
}

.approval-step-pending {
  color: #f59e0b;
  font-weight: 600;
}

.approval-step-approved {
  color: #22a06b;
  font-weight: 600;
}

.approval-step-rejected {
  color: #d65d8c;
  font-weight: 600;
}

:deep(.van-tabs__line) {
  background-color: rgb(242, 124, 52);
}
</style>

<style src="@/styles/approval-status.css"></style>
